import { getPaginationStrategy } from "./registry";
import { stripMathAnnotations } from "./htmlUtils";

/**
 * 測量前的 settle：把「會在測量後改變 DOM 高度」的非同步狀態先收斂，
 * 確保量到的是接近最終顯示的高度（ADR-0001 Step 2）。
 *
 * 目前只涵蓋字體載入（document.fonts.ready）：避免用 fallback 字體量出與最終
 * 不同的行高，這是離線版（本機字體即時可用）與網頁版（需下載）分頁不一致的
 * 來源之一（NBK-1464）。
 *
 * 為什麼「不」在這裡 await MathJax 佇列（重要，曾造成小視窗 tab 切換凍結）：
 * - SynopsisWindow 開在 Modal 裡、沒有自己的 <MathJax.Context>，只是借用全域
 *   window.MathJax.Hub，無權也無法保證該 Hub 的佇列正在排空。實測該全域佇列會
 *   卡在一個永不回呼的命令上（running=0 / pending=1 / queue 持續累積），導致
 *   `await Hub.Queue(resolve)` 永遠不 resolve，整條分頁鏈路靜默凍結。
 * - 且本階段測量仍在離屏 clone 進行，clone 內的算式本就未 typeset（見下），
 *   「排空 MathJax 佇列」對 clone 測量準確度毫無幫助——這個 await 從一開始就是
 *   加錯地方。要納入算式真實高度，是 Step 3（改測真實容器、等顯示端 typeset
 *   自己的完成 callback）的事，不該在此盲等全域 Hub。
 *
 * 注意：本階段測量仍在離屏 clone 進行，clone 內的 MathJax 並未 typeset，
 * 因此「算式真實高度」要到 Step 3（改測真實容器）才會被正確納入。
 */
const settle = async () => {
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {
      /* 忽略：fonts.ready 不支援或被拒絕時，退回原行為 */
    }
  }
};

/**
 * 分頁入口（ADR-0001 的 B 層引擎對外介面）。
 *
 * 依 layoutType 取得對應的 per-style 策略並執行；若該 layout 沒有註冊策略，
 * 回傳單頁（不分頁），行為與重構前 SynopsisWindow 的 fallback 分支一致。
 *
 * 演進路線（見 docs/adr/0001）：
 * - Step 1：行為等價，測量法沿用各 strategy 內既有的離屏 measureContainer。
 * - Step 2（目前）：在測量前 settle（僅字體載入；不在此等 MathJax 佇列，原因見 settle 註解）。
 *           註：clone 內的算式仍未 typeset，算式真實高度要到 Step 3 才正確。
 * - Step 3：把測量法換成真實顯示容器 + getClientRects，strategy 介面不需變動。
 *
 * @param {string} html - 原始 HTML 字串
 * @param {Object} options
 * @param {string} options.layoutType - 版型（LayoutType）
 * @param {number} options.lineHeight - 行高（px）
 * @param {number} options.pageSize - 每頁最大行數
 * @param {HTMLElement} options.referenceContainer - 量測用的參考容器
 * @returns {Promise<{htmlString: string, totalPages: number}>}
 */
export const paginate = async (
  html,
  { layoutType, lineHeight, pageSize, referenceContainer }
) => {
  const cleanHtml = stripMathAnnotations(html);
  const strategy = getPaginationStrategy(layoutType);

  if (!strategy) {
    return { htmlString: cleanHtml, totalPages: 1 };
  }

  // 測量前先 settle（字體載入），讓量到的高度接近最終顯示
  await settle();

  // strategy 可能回傳 Promise 或同步物件，await 對兩者皆適用
  return strategy(cleanHtml, lineHeight, pageSize, referenceContainer);
};
